/**
 * NoteService
 *
 * Manages private and public notes on person profiles.
 * Extends BaseContentService for standardized 10-step integration pattern.
 *
 * Features:
 * - Private notes (visible only to author)
 * - Public notes (visible to all, with approval workflow)
 * - Note pinning for important notes
 * - Rich text content support
 * - Color coding for notes
 * - Tag-based organization
 * - SystemConfig validation
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  serverTimestamp,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  Note,
  NoteVisibility,
  NoteFormData,
  NoteFilters,
  NoteApprovalStatus,
  NoteUpdateData,
} from '../types/note';
import { SystemConfig } from '../types';

import {
  BaseContentService,
  BaseContentEntity,
  CreateContentOptions,
} from './baseContentService';
import { SystemConfigService } from './systemConfigService';
import { ChangeTrackingService } from './changeTrackingService';
import { AuditLogService } from './auditLogService';
import { mentionService } from './mentionService';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Note entity extending base content entity
 */
export interface NoteEntity extends BaseContentEntity {
  // Association
  personName: string;

  // Content
  title: string;
  content: string;

  // Visibility
  isPrivate: boolean;
  visibility: NoteVisibility;

  // Organization
  tags: string[];
  isPinned: boolean;
  color?: string;

  // Authorship
  authorId: string;
  authorName: string;

  // Moderation (only for public notes)
  approvalStatus?: NoteApprovalStatus;
  approvedBy?: string;
  approvedAt?: Date;
  rejectionReason?: string;

  // Metadata
  lastEditedAt?: Date;

  // Soft delete
  deletedBy?: string;
  deletedAt?: Date;
}

/**
 * Note creation data
 */
interface NoteCreationData {
  personId: string;
  personName: string;
  title: string;
  content: string;
  isPrivate: boolean;
  visibility: NoteVisibility;
  tags: string[];
  isPinned: boolean;
  color?: string;
  authorName: string;
}

// ============================================================================
// NoteService Class
// ============================================================================

/**
 * NoteService - Manages note operations
 * Extends BaseContentService for 10-step compliance
 */
class NoteServiceImpl extends BaseContentService<NoteEntity> {
  // ============================================================================
  // ABSTRACT PROPERTIES IMPLEMENTATION
  // ============================================================================

  protected collectionName = 'notes';
  protected featureFlagKey: keyof SystemConfig['features'] = 'enableNotesTab';
  protected approvalConfigKey: keyof SystemConfig['content'] = 'requirePublicNoteApproval';
  protected maxPerPersonConfigKey: keyof SystemConfig['content'] = 'maxNotesPerPerson';
  protected authorFieldName = 'authorId'; // Notes use authorId as author field
  protected changeTrackingEntityType = 'note';

  // ============================================================================
  // ABSTRACT METHODS IMPLEMENTATION
  // ============================================================================

  /**
   * Validate note-specific data
   */
  protected async validateContent(data: NoteCreationData, config: SystemConfig): Promise<void> {
    // Validate text length
    const maxLength = config.content.maxNoteLength || 10000;
    if (data.content.length > maxLength) {
      throw new Error(`Note content exceeds maximum length of ${maxLength} characters`);
    }

    // Check if private/public notes are enabled
    if (data.isPrivate && !config.content.enablePrivateNotes) {
      throw new Error('Private notes are currently disabled');
    }

    if (!data.isPrivate && !config.content.enablePublicNotes) {
      throw new Error('Public notes are currently disabled');
    }

    // Validate required fields
    if (!data.title || !data.content) {
      throw new Error('title and content are required');
    }

    if (!data.personId) {
      throw new Error('personId is required for note creation');
    }
  }

  /**
   * Build Firestore query constraints from filters
   */
  protected buildFilters(filters: NoteFilters): QueryConstraint[] {
    const constraints: QueryConstraint[] = [];

    if (filters.visibility) {
      const isPrivate = filters.visibility === 'private';
      constraints.push(where('isPrivate', '==', isPrivate));
    }

    if (filters.authorId) {
      constraints.push(where('authorId', '==', filters.authorId));
    }

    if (filters.isPinned !== undefined) {
      constraints.push(where('isPinned', '==', filters.isPinned));
    }

    if (filters.approvalStatus) {
      constraints.push(where('approvalStatus', '==', filters.approvalStatus));
    }

    return constraints;
  }

  /**
   * Map Firestore document data to typed entity
   */
  protected mapDocumentToEntity(docData: any, docId: string): NoteEntity {
    return {
      id: docId,
      personId: docData.personId,
      personName: docData.personName,
      title: docData.title,
      content: docData.content,
      isPrivate: docData.isPrivate,
      visibility: docData.visibility,
      tags: docData.tags || [],
      isPinned: docData.isPinned || false,
      color: docData.color,
      authorId: docData.authorId,
      authorName: docData.authorName,
      approvalStatus: docData.approvalStatus,
      approvedBy: docData.approvedBy,
      approvedAt: docData.approvedAt ? this.timestampToDate(docData.approvedAt) : undefined,
      rejectionReason: docData.rejectionReason,
      createdAt: this.timestampToDate(docData.createdAt),
      updatedAt: this.timestampToDate(docData.updatedAt),
      lastEditedAt: docData.lastEditedAt ? this.timestampToDate(docData.lastEditedAt) : undefined,
      createdBy: docData.createdBy || docData.authorId, // Fallback for old data
      isApproved: docData.isApproved,
      isDeleted: docData.isDeleted,
      deletedBy: docData.deletedBy,
      deletedAt: docData.deletedAt ? this.timestampToDate(docData.deletedAt) : undefined,
    };
  }

  // ============================================================================
  // PUBLIC API METHODS
  // ============================================================================

  /**
   * Create a new note (private or public)
   * Follows complete 10-step integration pattern
   */
  async createNote(
    formData: NoteFormData,
    userId: string,
    userName: string,
    userRole: string
  ): Promise<Note> {
    try {
      // Prepare note data
      const noteData: NoteCreationData = {
        personId: formData.personId,
        personName: '', // Will be populated by UI
        title: formData.title || '',
        content: formData.content,
        isPrivate: formData.isPrivate,
        visibility: formData.isPrivate ? 'private' : 'public',
        tags: formData.tags || [],
        isPinned: false,
        color: formData.color,
        authorName: userName,
      };

      // Execute 10-step pattern via BaseContentService
      // Private notes skip approval; public notes go through approval workflow
      const createdEntity = await this.executeCreatePattern(
        noteData,
        userId,
        userName,
        userRole,
        {
          requiresApproval: !formData.isPrivate, // Private notes don't need approval
          entityTypeName: 'note',
          skipNotification: formData.isPrivate, // Don't notify for private notes
        }
      );

      // Convert to Note type
      const note: Note = {
        ...createdEntity,
        approvalStatus: createdEntity.isPrivate
          ? undefined
          : (createdEntity.isApproved ? 'approved' : 'pending'),
      } as unknown as Note;

      // Send mention notifications
      if (formData.mentions && formData.mentions.length > 0) {
        await mentionService.processMentions(formData.mentions, {
          contentType: 'note',
          contentId: note.id!,
          authorId: userId,
          authorName: userName,
          contentPreview: formData.content.substring(0, 100),
          targetType: 'person',
          targetId: formData.personId,
        });
      }

      return note;

    } catch (error: any) {
      console.error('Error creating note:', error);
      throw new Error(`Failed to create note: ${error.message}`);
    }
  }

  /**
   * Get all notes for a specific person
   */
  async getNotesByPerson(
    personId: string,
    filters?: NoteFilters,
    userId?: string
  ): Promise<Note[]> {
    try {
      const entities = await this.getByPerson(personId, filters, userId);

      // Filter out private notes unless they belong to the current user
      // Filter out pending public notes unless user is admin or author
      const filteredEntities = entities.filter((entity) => {
        if (entity.isPrivate && entity.authorId !== userId) {
          return false;
        }

        if (
          !entity.isPrivate &&
          entity.approvalStatus === 'pending' &&
          entity.authorId !== userId
        ) {
          return false;
        }

        return true;
      });

      return filteredEntities as unknown as Note[];
    } catch (error: any) {
      console.error('Error getting notes:', error);
      throw new Error(`Failed to get notes: ${error.message}`);
    }
  }

  /**
   * Get a specific note by ID
   */
  async getNoteById(noteId: string, userId?: string): Promise<Note | null> {
    try {
      const entity = await this.getById(noteId, userId);
      if (!entity) {
        return null;
      }

      // Check if user has permission to view private note
      if (entity.isPrivate && entity.authorId !== userId) {
        return null;
      }

      return entity as unknown as Note;
    } catch (error: any) {
      console.error('Error getting note:', error);
      throw new Error(`Failed to get note: ${error.message}`);
    }
  }

  /**
   * Update a note
   */
  async updateNote(
    noteId: string,
    updates: NoteUpdateData,
    userId: string,
    userName: string,
    userRole: string
  ): Promise<void> {
    try {
      // Get existing note
      const docRef = doc(db, this.collectionName, noteId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Note not found');
      }

      const existingNote = this.mapDocumentToEntity(docSnap.data(), docSnap.id);

      // Check permissions
      if (!this.checkOwnerOrAdmin(existingNote, userId, userRole)) {
        throw new Error('You do not have permission to update this note');
      }

      // Validate content length if being updated
      if (updates.content) {
        const config = await SystemConfigService.getConfig();
        const maxLength = config.content.maxNoteLength || 10000;
        if (updates.content.length > maxLength) {
          throw new Error(`Note content exceeds maximum length of ${maxLength} characters`);
        }
      }

      // Prepare update data
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp(),
        lastEditedAt: serverTimestamp(),
      };

      // Update in Firestore
      const { updateDoc } = await import('firebase/firestore');
      await updateDoc(docRef, this.removeUndefinedFields(updateData));

      // Get updated note for change tracking
      const updatedSnap = await getDoc(docRef);
      const updatedNote = this.mapDocumentToEntity(updatedSnap.data()!, updatedSnap.id);

      // Log change
      await ChangeTrackingService.logChange(
        {
          entityType: 'note',
          entityId: noteId,
          changeType: 'update',
          beforeState: existingNote,
          afterState: updatedNote,
        },
        userId,
        userName,
        true
      );

      // Log audit
      await AuditLogService.logAction({
        userId,
        action: 'content_approved',
        contentType: 'note',
        contentId: noteId,
        details: {
          operation: 'update',
          userName,
          personId: existingNote.personId,
          updates,
          isPrivate: existingNote.isPrivate,
        },
      });

      // Send mention notifications if mentions were updated
      if (updates.mentions && updates.mentions.length > 0 && updates.content) {
        await mentionService.processMentions(updates.mentions, {
          contentType: 'note',
          contentId: noteId,
          authorId: userId,
          authorName: userName,
          contentPreview: updates.content.substring(0, 100),
          targetType: 'person',
          targetId: existingNote.personId,
        });
      }

    } catch (error: any) {
      console.error('Error updating note:', error);
      throw new Error(`Failed to update note: ${error.message}`);
    }
  }

  /**
   * Pin or unpin a note
   */
  async togglePinNote(
    noteId: string,
    userId: string,
    userName: string,
    userRole: string
  ): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, noteId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Note not found');
      }

      const existingNote = this.mapDocumentToEntity(docSnap.data(), docSnap.id);

      // Check permissions
      if (!this.checkOwnerOrAdmin(existingNote, userId, userRole)) {
        throw new Error('You do not have permission to pin/unpin this note');
      }

      // Toggle pin status
      const { updateDoc } = await import('firebase/firestore');
      await updateDoc(docRef, {
        isPinned: !existingNote.isPinned,
        updatedAt: serverTimestamp(),
      });

      // Log change
      await ChangeTrackingService.logChange(
        {
          entityType: 'note',
          entityId: noteId,
          changeType: 'update',
          beforeState: existingNote,
          afterState: { ...existingNote, isPinned: !existingNote.isPinned },
        },
        userId,
        userName,
        true
      );

    } catch (error: any) {
      console.error('Error toggling pin status:', error);
      throw new Error(`Failed to toggle pin status: ${error.message}`);
    }
  }

  /**
   * Soft delete a note
   */
  async deleteNote(
    noteId: string,
    userId: string,
    userName: string,
    userRole: string
  ): Promise<void> {
    try {
      // Use BaseContentService soft delete (handles change tracking and audit logging)
      await this.softDelete(noteId, userId, userName, userRole);
    } catch (error: any) {
      console.error('Error deleting note:', error);
      throw new Error(`Failed to delete note: ${error.message}`);
    }
  }

  /**
   * Approve a pending public note (admin only)
   */
  async approveNote(
    noteId: string,
    userId: string,
    userName: string
  ): Promise<void> {
    try {
      // Get note to check if it's private
      const entity = await this.getById(noteId);
      if (!entity) {
        throw new Error('Note not found');
      }

      if (entity.isPrivate) {
        throw new Error('Private notes do not require approval');
      }

      // Use BaseContentService approve method
      await this.approve(noteId, userId, userName, 'admin');

      // Update approval status field (Note-specific)
      const docRef = doc(db, this.collectionName, noteId);
      const { updateDoc } = await import('firebase/firestore');
      await updateDoc(docRef, {
        approvalStatus: 'approved',
        updatedAt: serverTimestamp(),
      });

    } catch (error: any) {
      console.error('Error approving note:', error);
      throw new Error(`Failed to approve note: ${error.message}`);
    }
  }

  /**
   * Reject a pending public note (admin only)
   */
  async rejectNote(
    noteId: string,
    reason: string,
    userId: string,
    userName: string
  ): Promise<void> {
    try {
      // Get note to check if it's private
      const entity = await this.getById(noteId);
      if (!entity) {
        throw new Error('Note not found');
      }

      if (entity.isPrivate) {
        throw new Error('Private notes do not require approval');
      }

      // Use BaseContentService reject method
      await this.reject(noteId, reason, userId, userName, 'admin');

      // Update approval status field (Note-specific)
      const docRef = doc(db, this.collectionName, noteId);
      const { updateDoc } = await import('firebase/firestore');
      await updateDoc(docRef, {
        approvalStatus: 'rejected',
        updatedAt: serverTimestamp(),
      });

    } catch (error: any) {
      console.error('Error rejecting note:', error);
      throw new Error(`Failed to reject note: ${error.message}`);
    }
  }

  /**
   * Get all public notes awaiting approval (admin only)
   */
  async getNotesAwaitingApproval(limit?: number): Promise<Note[]> {
    try {
      // Get unapproved notes using base method
      const entities = await this.getAwaitingApproval(limit || 50);

      // Filter out private notes (private notes don't need approval)
      const publicEntities = entities.filter(entity => !entity.isPrivate);

      return publicEntities as unknown as Note[];
    } catch (error: any) {
      console.error('Error getting pending notes:', error);
      throw new Error(`Failed to get pending notes: ${error.message}`);
    }
  }
}

// ============================================================================
// SINGLETON EXPORT
// ============================================================================

/**
 * Singleton instance of NoteService
 */
export const NoteService = new NoteServiceImpl();
